import { useState } from 'react';
import { Box, CloseButton, Flex, Textarea } from '@chakra-ui/react';
import { Button, Colors, Text } from '@mercator-gcp/grout';
import AIInteractionAPI, {
  ScoutAgentFeedbackRating,
} from 'services/API/AIInteractionAPI';

const containerStyle = {
  alignItems: 'center',
  gap: 'padding_4',
  marginTop: 'padding_4',
};

const thumbButtonStyle = (color: string, isSelected: boolean) => ({
  padding: 'padding_4',
  height: 'auto',
  minWidth: 'auto',
  color,
  opacity: isSelected ? 1 : 0.6,
  transition: 'color 0.15s ease, opacity 0.15s ease',
  _hover: {
    opacity: 1,
  },
});

const THUMB_UP_PATH =
  'M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z';

const THUMB_DOWN_PATH =
  'M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z';

const ThumbIcon = ({ direction }: { direction: 'up' | 'down' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d={direction === 'up' ? THUMB_UP_PATH : THUMB_DOWN_PATH} />
  </svg>
);

const reasonBoxStyle = {
  marginTop: 'padding_4',
  padding: 'padding_8',
  border: `1px solid ${Colors.ArmyGreen300}`,
  borderRadius: '8px',
  backgroundColor: Colors.White,
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 'padding_8',
  maxWidth: '320px',
};

const reasonHeaderStyle = {
  alignItems: 'center',
  justifyContent: 'space-between',
};

const reasonTextareaStyle = {
  border: `1px solid ${Colors.ArmyGreen300}`,
  borderRadius: '6px',
  padding: 'padding_4 padding_8',
  minHeight: '56px',
  fontSize: '13px',
  _focus: {
    border: `1px solid ${Colors.ArmyGreen300}`,
    outline: 'none',
  },
};

type FeedbackControlProps = {
  sessionId: string;
  eventTimestamp?: string;
};

/**
 * Fire-and-forget: the endpoint is a one-way write for analytics/debugging, so
 * failures are intentionally swallowed and the UI never shows a loading/error state.
 */
const sendFeedback = (
  sessionId: string,
  rating: ScoutAgentFeedbackRating,
  eventTimestamp?: string,
  reason?: string,
) => {
  AIInteractionAPI.postScoutAgentFeedback({
    sessionId,
    rating,
    eventTimestamp,
    reason: reason || undefined,
  }).catch(() => {});
};

const FeedbackControl = ({ sessionId, eventTimestamp }: FeedbackControlProps) => {
  const [rating, setRating] = useState<ScoutAgentFeedbackRating | null>(null);
  const [isReasonBoxOpen, setIsReasonBoxOpen] = useState(false);
  const [reason, setReason] = useState('');

  const handleThumbsUp = () => {
    setRating('up');
    setIsReasonBoxOpen(false);
    sendFeedback(sessionId, 'up', eventTimestamp);
  };

  const handleThumbsDown = () => {
    setRating('down');
    setIsReasonBoxOpen(true);
    sendFeedback(sessionId, 'down', eventTimestamp);
  };

  const closeReasonBox = () => {
    setIsReasonBoxOpen(false);
    setReason('');
  };

  const handleReasonSubmit = () => {
    sendFeedback(sessionId, 'down', eventTimestamp, reason.trim());
    closeReasonBox();
  };

  return (
    <Box>
      <Flex sx={containerStyle}>
        <Button
          aria-label="Thumbs up"
          variant="unstyled"
          onClick={handleThumbsUp}
          sx={thumbButtonStyle(
            rating === 'up' ? Colors.SystemColorPositive : Colors.TextQuiet,
            rating === 'up',
          )}
        >
          <ThumbIcon direction="up" />
        </Button>
        <Button
          aria-label="Thumbs down"
          variant="unstyled"
          onClick={handleThumbsDown}
          sx={thumbButtonStyle(
            rating === 'down' ? Colors.SystemColorNegative : Colors.TextQuiet,
            rating === 'down',
          )}
        >
          <ThumbIcon direction="down" />
        </Button>
      </Flex>
      {isReasonBoxOpen && (
        <Box sx={reasonBoxStyle}>
          <Flex sx={reasonHeaderStyle}>
            <Text.Small color={Colors.TextQuiet}>What went wrong? (optional)</Text.Small>
            <CloseButton size="sm" onClick={closeReasonBox} aria-label="Dismiss" />
          </Flex>
          <Textarea
            aria-label="Feedback reason"
            placeholder="Tell us what went wrong..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={reasonTextareaStyle}
            resize="vertical"
          />
          <Button
            variant="secondary"
            onClick={handleReasonSubmit}
            sx={{ alignSelf: 'flex-end' }}
          >
            Send
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FeedbackControl;
